/**
 * Background motion effects — independent of color skins (VividLex pattern).
 */

export type BgFxId =
  | "orbs"
  | "mesh"
  | "aurora"
  | "stars"
  | "sparkles"
  | "grid"
  | "ribbons"
  | "hex"
  | "rain"
  | "flame"
  | "scan"
  | "fractal"
  | "spiral"
  | "frequency"
  | "still"
  | "none";

export interface BgFxOption {
  id: BgFxId;
  name: string;
  blurb: string;
  preview: "soft" | "wave" | "dots" | "lines" | "geo" | "fire" | "flat";
}

export const BG_FX_OPTIONS: BgFxOption[] = [
  { id: "orbs", name: "Orbs", blurb: "Soft glowing balls drifting.", preview: "soft" },
  { id: "mesh", name: "Color mesh", blurb: "Living gradient wash.", preview: "wave" },
  { id: "aurora", name: "Aurora", blurb: "Flowing light ribbons.", preview: "wave" },
  { id: "stars", name: "Stars", blurb: "Twinkling field with slow drift.", preview: "dots" },
  { id: "sparkles", name: "Spark field", blurb: "Rising glitter particles.", preview: "dots" },
  { id: "grid", name: "Cyber grid", blurb: "Perspective floor + scanline.", preview: "lines" },
  { id: "ribbons", name: "Ribbons", blurb: "Diagonal neon bands.", preview: "wave" },
  { id: "hex", name: "Hex lattice", blurb: "Honeycomb geometry.", preview: "geo" },
  { id: "rain", name: "Neon rain", blurb: "Soft colored streaks.", preview: "lines" },
  { id: "flame", name: "Ember rise", blurb: "Rising heat particles.", preview: "fire" },
  { id: "scan", name: "Radar scan", blurb: "Horizontal sweep.", preview: "lines" },
  { id: "fractal", name: "Fractal", blurb: "Nested glow tiles.", preview: "geo" },
  { id: "spiral", name: "Spiral", blurb: "Slow rotating wash.", preview: "wave" },
  { id: "frequency", name: "Frequency field", blurb: "EQ bars pulsing.", preview: "lines" },
  { id: "still", name: "Still", blurb: "No background motion.", preview: "flat" },
];

const STORAGE_KEY = "musicforge.bg_fx.v1";
const VALID = new Set(BG_FX_OPTIONS.map((o) => o.id));

export const SKIN_DEFAULT_FX: Record<string, BgFxId> = {
  "forge-neon": "orbs",
  "ink-night": "stars",
  aurora: "aurora",
  parchment: "mesh",
  "minimal-light": "still",
  "plasma-ember": "flame",
  "void-matrix": "grid",
  "cold-steel": "scan",
};

export function getBgFxId(): BgFxId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && VALID.has(v as BgFxId)) return v as BgFxId;
  } catch {
    /* ignore */
  }
  return "orbs";
}

export function setBgFxId(id: BgFxId): void {
  if (!VALID.has(id)) return;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  applyBgFx(id);
  try {
    window.dispatchEvent(new CustomEvent("musicforge-bg-fx", { detail: { id } }));
  } catch {
    /* ignore */
  }
}

export function applyBgFx(id?: BgFxId): void {
  const root = document.documentElement;
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const requested = id ?? getBgFxId();
  const effective: BgFxId = reduced || requested === "still" ? "none" : requested;
  root.setAttribute("data-bg-fx", effective);
}

export function bootstrapBgFx(): BgFxId {
  const id = getBgFxId();
  applyBgFx(id);
  return id;
}

export function getBgFxOption(id: BgFxId): BgFxOption {
  return BG_FX_OPTIONS.find((o) => o.id === id) ?? BG_FX_OPTIONS[0]!;
}

export function applySuggestedFxForSkin(skinId: string): BgFxId {
  const fx = SKIN_DEFAULT_FX[skinId] ?? "orbs";
  setBgFxId(fx);
  return fx;
}
